/**
 * Canonical Planner activity catalogue (master spec §"Canonical Planner
 * activity catalogue", CONTRACTS.md §4/§8).
 *
 * Rules baked in:
 * - `Dweilen`, never `Dwijlen`.
 * - `Bad nemen` sits directly adjacent to `Douchen` (Verzorging).
 * - `Opstaan` retained; legacy `Wakker worden` maps here via aliases.
 * - No `Pomodoro` entry; legacy items map to `focusblok` via aliases.
 * - `WhatsApp bekijken` carries `badge: 'warning'`.
 * - Order inside a category is the canonical deck order — do not sort.
 *
 * Ids are stable kebab-case and never change once shipped: saved planner
 * items reference them. Migrate via ACTIVITY_ID_ALIASES instead of renaming.
 */
import type { Activity, ActivityCategory } from './types'

export const ACTIVITY_CATEGORIES: ActivityCategory[] = [
  { id: 'eten-en-drinken', name: 'Eten en drinken' },
  { id: 'gezondheid', name: 'Gezondheid' },
  { id: 'verzorging', name: 'Verzorging' },
  { id: 'rust', name: 'Rust' },
  { id: 'beweging', name: 'Beweging' },
  { id: 'werk-en-studie', name: 'Werk en studie' },
  { id: 'huishouden', name: 'Huishouden' },
  { id: 'sociaal', name: 'Sociaal' },
  { id: 'therapie-en-zelfzorg', name: 'Therapie en zelfzorg' },
  { id: 'buiten-en-dier', name: 'Buiten en dier' },
  { id: 'slapen', name: 'Slapen' },
  { id: 'reizen-en-verplaatsing', name: 'Reizen en verplaatsing' },
  { id: 'administratie', name: 'Administratie' },
  { id: 'creatief', name: 'Creatief' },
  { id: 'afspraken', name: 'Afspraken' },
  { id: 'extra', name: 'Extra' },
]

/**
 * Calm semantic colors per category, all taken from the design-token palette
 * (src/styles/tokens.css). Color is decorative only — activity names are
 * always rendered as text.
 */
const CATEGORY_COLORS: Record<string, string> = {
  'eten-en-drinken': '#c99a4a', // --warning (warm)
  gezondheid: '#5fa372', // --success
  verzorging: '#89afc9', // --mist-blue
  rust: '#4e7291', // --soft-blue
  beweging: '#263545', // --granite-blue
  'werk-en-studie': '#1a2737', // --muted-navy
  huishouden: '#4e7291',
  sociaal: '#c99a4a',
  'therapie-en-zelfzorg': '#5fa372',
  'buiten-en-dier': '#89afc9',
  slapen: '#1a2737',
  'reizen-en-verplaatsing': '#263545',
  administratie: '#4e7291',
  creatief: '#c99a4a',
  afspraken: '#5fa372',
  extra: '#89afc9',
}

type RawActivity = {
  id: string
  name: string
  categoryId: string
  iconId: string
  badge?: 'warning'
}

const RAW_ACTIVITIES: RawActivity[] = [
  // Eten en drinken
  { id: 'ontbijt', name: 'Ontbijt', categoryId: 'eten-en-drinken', iconId: 'bowl' },
  { id: 'lunch', name: 'Lunch', categoryId: 'eten-en-drinken', iconId: 'plate' },
  { id: 'avondeten', name: 'Avondeten', categoryId: 'eten-en-drinken', iconId: 'cloche' },
  { id: 'snack', name: 'Snack', categoryId: 'eten-en-drinken', iconId: 'apple' },
  { id: 'water-drinken', name: 'Water drinken', categoryId: 'eten-en-drinken', iconId: 'glass-water' },
  { id: 'koffie', name: 'Koffie', categoryId: 'eten-en-drinken', iconId: 'coffee' },
  { id: 'thee', name: 'Thee', categoryId: 'eten-en-drinken', iconId: 'tea' },
  { id: 'smoothie', name: 'Smoothie', categoryId: 'eten-en-drinken', iconId: 'smoothie' },
  { id: 'koken', name: 'Koken', categoryId: 'eten-en-drinken', iconId: 'pot' },
  { id: 'eten-voorbereiden', name: 'Eten voorbereiden', categoryId: 'eten-en-drinken', iconId: 'cutting-board' },
  // Gezondheid
  { id: 'medicatie', name: 'Medicatie', categoryId: 'gezondheid', iconId: 'pill' },
  { id: 'supplement', name: 'Supplement', categoryId: 'gezondheid', iconId: 'bottle' },
  { id: 'creatine', name: 'Creatine', categoryId: 'gezondheid', iconId: 'jar' },
  { id: 'medicijn-ophalen', name: 'Medicijn ophalen', categoryId: 'gezondheid', iconId: 'pharmacy' },
  { id: 'medicatie-innemen', name: 'Medicatie innemen', categoryId: 'gezondheid', iconId: 'pill-check' },
  { id: 'gezondheidscheck', name: 'Gezondheidscheck', categoryId: 'gezondheid', iconId: 'heart-pulse' },
  { id: 'water-bijvullen', name: 'Water bijvullen', categoryId: 'gezondheid', iconId: 'refill-droplet' },
  // Verzorging — Douchen en Bad nemen staan bewust direct naast elkaar.
  { id: 'douchen', name: 'Douchen', categoryId: 'verzorging', iconId: 'shower' },
  { id: 'bad-nemen', name: 'Bad nemen', categoryId: 'verzorging', iconId: 'bath' },
  { id: 'tanden-poetsen', name: 'Tanden poetsen', categoryId: 'verzorging', iconId: 'toothbrush' },
  { id: 'huidverzorging', name: 'Huidverzorging', categoryId: 'verzorging', iconId: 'cream' },
  { id: 'haar-verzorgen', name: 'Haar verzorgen', categoryId: 'verzorging', iconId: 'comb' },
  { id: 'scheren', name: 'Scheren', categoryId: 'verzorging', iconId: 'razor' },
  { id: 'aankleden', name: 'Aankleden', categoryId: 'verzorging', iconId: 'shirt' },
  { id: 'opstaan', name: 'Opstaan', categoryId: 'verzorging', iconId: 'sunrise' },
  // Rust
  { id: 'rustmoment', name: 'Rustmoment', categoryId: 'rust', iconId: 'cushion' },
  { id: 'lezen', name: 'Lezen', categoryId: 'rust', iconId: 'book' },
  { id: 'pauze', name: 'Pauze', categoryId: 'rust', iconId: 'pause' },
  { id: 'uitrusten', name: 'Uitrusten', categoryId: 'rust', iconId: 'armchair' },
  { id: 'ontspanning', name: 'Ontspanning', categoryId: 'rust', iconId: 'leaf' },
  { id: 'podcast-luisteren', name: 'Podcast luisteren', categoryId: 'rust', iconId: 'mic' },
  { id: 'muziek-luisteren', name: 'Muziek luisteren', categoryId: 'rust', iconId: 'music-note' },
  { id: 'schermpauze', name: 'Schermpauze', categoryId: 'rust', iconId: 'screen-off' },
  // Beweging
  { id: 'wandelen', name: 'Wandelen', categoryId: 'beweging', iconId: 'walk' },
  { id: 'sporten', name: 'Sporten', categoryId: 'beweging', iconId: 'dumbbell' },
  { id: 'yoga', name: 'Yoga', categoryId: 'beweging', iconId: 'lotus' },
  { id: 'stretchen', name: 'Stretchen', categoryId: 'beweging', iconId: 'stretch' },
  { id: 'fietsen', name: 'Fietsen', categoryId: 'beweging', iconId: 'bike' },
  { id: 'zwemmen', name: 'Zwemmen', categoryId: 'beweging', iconId: 'waves' },
  { id: 'buitenlucht', name: 'Buitenlucht', categoryId: 'beweging', iconId: 'tree-sun' },
  // Werk en studie
  { id: 'werken', name: 'Werken', categoryId: 'werk-en-studie', iconId: 'briefcase' },
  { id: 'focusblok', name: 'Focusblok', categoryId: 'werk-en-studie', iconId: 'target' },
  { id: 'computer', name: 'Computer', categoryId: 'werk-en-studie', iconId: 'monitor' },
  { id: 'e-mail', name: 'E-mail', categoryId: 'werk-en-studie', iconId: 'mail' },
  { id: 'mailen', name: 'Mailen', categoryId: 'werk-en-studie', iconId: 'mail-send' },
  { id: 'telefoon', name: 'Telefoon', categoryId: 'werk-en-studie', iconId: 'phone' },
  { id: 'vergadering', name: 'Vergadering', categoryId: 'werk-en-studie', iconId: 'people' },
  { id: 'meeting', name: 'Meeting', categoryId: 'werk-en-studie', iconId: 'people-chat' },
  { id: 'notities', name: 'Notities', categoryId: 'werk-en-studie', iconId: 'note' },
  { id: 'laptopwerk', name: 'Laptopwerk', categoryId: 'werk-en-studie', iconId: 'laptop' },
  { id: 'notities-maken', name: 'Notities maken', categoryId: 'werk-en-studie', iconId: 'note-pencil' },
  { id: 'bureau-opruimen', name: 'Bureau opruimen', categoryId: 'werk-en-studie', iconId: 'desk' },
  { id: 'studie', name: 'Studie', categoryId: 'werk-en-studie', iconId: 'cap' },
  // Huishouden
  { id: 'opruimen', name: 'Opruimen', categoryId: 'huishouden', iconId: 'box-tidy' },
  { id: 'afwassen', name: 'Afwassen', categoryId: 'huishouden', iconId: 'dishes' },
  { id: 'was-doen', name: 'Was doen', categoryId: 'huishouden', iconId: 'washer' },
  { id: 'stofzuigen', name: 'Stofzuigen', categoryId: 'huishouden', iconId: 'vacuum' },
  { id: 'boodschappen', name: 'Boodschappen', categoryId: 'huishouden', iconId: 'bag' },
  { id: 'schoonmaken', name: 'Schoonmaken', categoryId: 'huishouden', iconId: 'sparkle' },
  { id: 'vuilnis', name: 'Vuilnis', categoryId: 'huishouden', iconId: 'trash' },
  { id: 'planten-water-geven', name: 'Planten water geven', categoryId: 'huishouden', iconId: 'plant' },
  { id: 'huishoudtaak', name: 'Huishoudtaak', categoryId: 'huishouden', iconId: 'checklist' },
  { id: 'tuinieren', name: 'Tuinieren', categoryId: 'huishouden', iconId: 'trowel' },
  { id: 'dweilen', name: 'Dweilen', categoryId: 'huishouden', iconId: 'mop' },
  { id: 'droger', name: 'Droger', categoryId: 'huishouden', iconId: 'dryer' },
  { id: 'bed-verschonen', name: 'Bed verschonen', categoryId: 'huishouden', iconId: 'bed-refresh' },
  // Sociaal
  { id: 'sociaal-contact', name: 'Sociaal contact', categoryId: 'sociaal', iconId: 'chats' },
  { id: 'bellen', name: 'Bellen', categoryId: 'sociaal', iconId: 'phone-waves' },
  { id: 'gesprek', name: 'Gesprek', categoryId: 'sociaal', iconId: 'chat' },
  { id: 'feest', name: 'Feest', categoryId: 'sociaal', iconId: 'balloon' },
  { id: 'winkelen', name: 'Winkelen', categoryId: 'sociaal', iconId: 'store' },
  {
    id: 'whatsapp-bekijken',
    name: 'WhatsApp bekijken',
    categoryId: 'sociaal',
    iconId: 'message',
    badge: 'warning',
  },
  // Therapie en zelfzorg
  { id: 'therapie', name: 'Therapie', categoryId: 'therapie-en-zelfzorg', iconId: 'chat-heart' },
  { id: 'meditatie', name: 'Meditatie', categoryId: 'therapie-en-zelfzorg', iconId: 'person-lotus' },
  { id: 'ademhaling', name: 'Ademhaling', categoryId: 'therapie-en-zelfzorg', iconId: 'wind' },
  { id: 'zelfzorg', name: 'Zelfzorg', categoryId: 'therapie-en-zelfzorg', iconId: 'heart-hand' },
  { id: 'dagboek-schrijven', name: 'Dagboek schrijven', categoryId: 'therapie-en-zelfzorg', iconId: 'journal' },
  { id: 'dankbaarheid', name: 'Dankbaarheid', categoryId: 'therapie-en-zelfzorg', iconId: 'heart-sparkle' },
  // Buiten en dier
  { id: 'hond-uitlaten', name: 'Hond uitlaten', categoryId: 'buiten-en-dier', iconId: 'paw' },
  { id: 'huisdier-verzorgen', name: 'Huisdier verzorgen', categoryId: 'buiten-en-dier', iconId: 'paw' },
  { id: 'natuur', name: 'Natuur', categoryId: 'buiten-en-dier', iconId: 'tree' },
  { id: 'tuin', name: 'Tuin', categoryId: 'buiten-en-dier', iconId: 'flower' },
  { id: 'balkon', name: 'Balkon', categoryId: 'buiten-en-dier', iconId: 'plant-pot' },
  // Slapen
  { id: 'slapen', name: 'Slapen', categoryId: 'slapen', iconId: 'moon' },
  { id: 'dutje', name: 'Dutje', categoryId: 'slapen', iconId: 'zzz' },
  { id: 'naar-bed', name: 'Naar bed', categoryId: 'slapen', iconId: 'bed' },
  { id: 'slaaproutine', name: 'Slaaproutine', categoryId: 'slapen', iconId: 'moon-stars' },
  // Reizen en verplaatsing
  { id: 'vertrekken', name: 'Vertrekken', categoryId: 'reizen-en-verplaatsing', iconId: 'door-out' },
  { id: 'thuiskomen', name: 'Thuiskomen', categoryId: 'reizen-en-verplaatsing', iconId: 'home' },
  { id: 'reizen', name: 'Reizen', categoryId: 'reizen-en-verplaatsing', iconId: 'suitcase' },
  { id: 'scooter', name: 'Scooter', categoryId: 'reizen-en-verplaatsing', iconId: 'scooter' },
  { id: 'fietsen-vervoer', name: 'Fietsen', categoryId: 'reizen-en-verplaatsing', iconId: 'bike' },
  { id: 'huis-verlaten', name: 'Huis verlaten', categoryId: 'reizen-en-verplaatsing', iconId: 'house-arrow' },
  // Administratie
  { id: 'administratie', name: 'Administratie', categoryId: 'administratie', iconId: 'folder' },
  { id: 'pakket-zoeken', name: 'Pakket zoeken', categoryId: 'administratie', iconId: 'package' },
  { id: 'opladen', name: 'Opladen', categoryId: 'administratie', iconId: 'plug' },
  { id: 'tas-inpakken', name: 'Tas inpakken', categoryId: 'administratie', iconId: 'backpack' },
  { id: 'documenten-regelen', name: 'Documenten regelen', categoryId: 'administratie', iconId: 'documents' },
  // Creatief
  { id: 'creatief-bezig', name: 'Creatief bezig', categoryId: 'creatief', iconId: 'palette' },
  { id: 'muziek', name: 'Muziek', categoryId: 'creatief', iconId: 'music-note' },
  { id: 'tekenen', name: 'Tekenen', categoryId: 'creatief', iconId: 'pencil' },
  { id: 'schilderen', name: 'Schilderen', categoryId: 'creatief', iconId: 'brush' },
  { id: 'fotografie', name: 'Fotografie', categoryId: 'creatief', iconId: 'camera' },
  // Afspraken
  { id: 'afspraak', name: 'Afspraak', categoryId: 'afspraken', iconId: 'calendar' },
  { id: 'huisarts', name: 'Huisarts', categoryId: 'afspraken', iconId: 'medical-cross' },
  { id: 'tandarts', name: 'Tandarts', categoryId: 'afspraken', iconId: 'tooth' },
  { id: 'begeleiding', name: 'Begeleiding', categoryId: 'afspraken', iconId: 'hand-heart' },
  { id: 'therapie-afspraak', name: 'Therapie-afspraak', categoryId: 'afspraken', iconId: 'calendar-chat' },
  // Extra
  { id: 'game-spelen', name: 'Game spelen', categoryId: 'extra', iconId: 'gamepad' },
]

export const ACTIVITIES: Activity[] = RAW_ACTIVITIES.map((activity) => ({
  ...activity,
  color: CATEGORY_COLORS[activity.categoryId] ?? '#4e7291',
}))
